// Path: C:\gestion-copro\src\services\echeance.service.ts
// Service Calendrier / Échéances VIGIE
// Session 130 : Création — CRUD manuelles + agrégation automatiques (garanties, courriers, dossiers)
// Session 130b : Fix garanties — support Timestamp Firestore + string + logs diagnostiques
// Session 179 : Propagation champ resolu depuis les documents source (courriers, dossiers)
// Session 180 : Protection anti-doublon dans creerEcheance()

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Echeance,
  EcheanceCreate,
  calculerJoursRestants,
  calculerUrgence,
} from '../types/echeance.types';

// ============================================
// COLLECTION FIRESTORE
// ============================================

const COLLECTION = 'echeances';

// ============================================
// CRUD — ÉCHÉANCES MANUELLES
// ============================================

/**
 * Créer une échéance manuelle
 * Session 180 : Protection anti-doublon — vérifie qu'il n'existe pas déjà
 * une échéance manuelle avec le même titre + même date + même copropriété.
 * Si doublon détecté, retourne l'id existant sans créer.
 */
export const creerEcheance = async (data: EcheanceCreate): Promise<string> => {
  // --- Protection anti-doublon ---
  if (data.coproprieteId && data.titre && data.dateEcheance) {
    try {
      const q = query(
        collection(db, COLLECTION),
        where('coproprieteId', '==', data.coproprieteId),
        where('titre', '==', data.titre),
        where('source', '==', data.source || 'manuelle')
      );
      const snap = await getDocs(q);

      // Comparer les dates (même jour)
      const nouvelleDateMs = data.dateEcheance instanceof Timestamp
        ? data.dateEcheance.toDate().getTime()
        : new Date(data.dateEcheance as any).getTime();
      const nouvelleDate = new Date(nouvelleDateMs).toISOString().split('T')[0];

      for (const d of snap.docs) {
        const existante = d.data();
        const existanteDateRaw = existante.dateEcheance?.toDate?.()
          || new Date(existante.dateEcheance as any);
        const existanteDate = existanteDateRaw.toISOString().split('T')[0];

        if (existanteDate === nouvelleDate) {
          console.log(`[Calendrier] Doublon détecté pour "${data.titre}" le ${nouvelleDate} — retourne id existant: ${d.id}`);
          return d.id;
        }
      }
    } catch (error) {
      // En cas d'erreur de vérification (index manquant par ex.), on crée quand même
      console.warn('[Calendrier] Erreur vérification doublon, création normale:', error);
    }
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

/** Modifier une échéance manuelle */
export const modifierEcheance = async (
  id: string,
  data: Partial<Omit<Echeance, 'id' | 'createdAt'>>
): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, id), {
    ...data,
    updatedAt: Timestamp.now(),
  });
};

/** Supprimer une échéance manuelle */
export const supprimerEcheance = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION, id));
};

/** Marquer une échéance comme terminée / non terminée */
export const toggleTerminee = async (id: string, terminee: boolean): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, id), {
    terminee,
    updatedAt: Timestamp.now(),
  });
};

// ============================================
// UTILITAIRE — Parser une date multi-format
// ============================================

/** Parse une valeur Firestore en Date — supporte string ISO, Timestamp, objet {seconds} */
const parseFirestoreDate = (valeur: any): Date | null => {
  if (!valeur) return null;

  // 1. String ISO (ex: "2026-09-15")
  if (typeof valeur === 'string' && valeur.trim()) {
    const d = new Date(valeur);
    return isNaN(d.getTime()) ? null : d;
  }

  // 2. Timestamp Firestore avec .toDate()
  if (typeof valeur === 'object' && typeof valeur.toDate === 'function') {
    return valeur.toDate();
  }

  // 3. Timestamp sérialisé {seconds, nanoseconds}
  if (typeof valeur === 'object' && typeof valeur.seconds === 'number') {
    return new Date(valeur.seconds * 1000);
  }

  // 4. Number (milliseconds epoch)
  if (typeof valeur === 'number') {
    return new Date(valeur);
  }

  return null;
};

// ============================================
// CHARGEMENT — ÉCHÉANCES MANUELLES FIRESTORE
// ============================================

/** Charger toutes les échéances manuelles d'une copropriété */
const chargerEcheancesManuelles = async (coproprieteId: string): Promise<Echeance[]> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('coproprieteId', '==', coproprieteId),
      orderBy('dateEcheance', 'asc')
    );
    const snap = await getDocs(q);
    console.log(`[Calendrier] Échéances manuelles trouvées: ${snap.size}`);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    } as Echeance));
  } catch (error) {
    // L'index Firestore n'existe peut-être pas encore — fallback sans orderBy
    console.warn('[Calendrier] Index manquant pour echeances, fallback sans tri:', error);
    try {
      const q = query(
        collection(db, COLLECTION),
        where('coproprieteId', '==', coproprieteId)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      } as Echeance));
    } catch (fallbackError) {
      console.error('[Calendrier] Erreur chargement manuelles:', fallbackError);
      return [];
    }
  }
};

// ============================================
// AGRÉGATION — ÉCHÉANCES AUTOMATIQUES
// ============================================

/** Construire les échéances depuis les garanties constructeur (coproprietes.garanties) */
const agregerGaranties = async (coproprieteId: string): Promise<Echeance[]> => {
  const echeances: Echeance[] = [];
  
  try {
    const coproSnap = await getDoc(doc(db, 'coproprietes', coproprieteId));
    console.log(`[Calendrier] Document copropriete ${coproprieteId} existe: ${coproSnap.exists()}`);
    
    if (!coproSnap.exists()) return echeances;
    
    const data = coproSnap.data();
    const garanties = data?.garanties;
    console.log('[Calendrier] Champ garanties brut:', JSON.stringify(garanties));

    if (!garanties) {
      console.log('[Calendrier] Pas de champ garanties dans le document copropriete');
      return echeances;
    }

    const configs: { champ: string; label: string }[] = [
      { champ: 'gpa', label: 'Garantie de Parfait Achèvement (GPA)' },
      { champ: 'biennale', label: 'Garantie biennale' },
      { champ: 'decennale', label: 'Garantie décennale' },
    ];

    for (const cfg of configs) {
      const valeur = garanties[cfg.champ];
      console.log(`[Calendrier] Garantie ${cfg.champ}: valeur=${JSON.stringify(valeur)}, type=${typeof valeur}`);
      
      const dateExp = parseFirestoreDate(valeur);
      
      if (!dateExp) {
        console.log(`[Calendrier] Garantie ${cfg.champ}: date non parsable, ignorée`);
        continue;
      }

      console.log(`[Calendrier] Garantie ${cfg.champ}: date parsée = ${dateExp.toISOString()}`);

      echeances.push({
        id: `garantie_${cfg.champ}`,
        coproprieteId,
        titre: cfg.label,
        description: `Expire le ${dateExp.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}. Toutes les malfaçons doivent être signalées avant cette date.`,
        dateEcheance: Timestamp.fromDate(dateExp),
        source: 'garantie',
        sourceId: cfg.champ,
        sourceLabel: cfg.label.split('(')[0].trim(),
        terminee: false,
        createdAt: Timestamp.now(),
      });
    }
    
    console.log(`[Calendrier] Garanties agrégées: ${echeances.length}`);
  } catch (error) {
    console.error('[Calendrier] Erreur agrégation garanties:', error);
  }

  return echeances;
};

/** Construire les échéances depuis les courriers envoyés en attente de réponse */
const agregerCourriers = async (coproprieteId: string): Promise<Echeance[]> => {
  const echeances: Echeance[] = [];

  try {
    const q = query(
      collection(db, 'courriers'),
      where('coproprieteId', '==', coproprieteId),
      where('statut', '==', 'envoye')
    );
    const snap = await getDocs(q);
    console.log(`[Calendrier] Courriers envoyés trouvés: ${snap.size}`);

    for (const d of snap.docs) {
      const data = d.data();
      
      // Date d'envoi
      const dateEnvoi = parseFirestoreDate(data.dateEnvoi) || parseFirestoreDate(data.createdAt);
      if (!dateEnvoi) continue;

      // Calcul délai raisonnable selon le mode d'envoi
      // Valeurs exactes du formulaire : 'email', 'postal', 'lrar', 'recommande_ar'
      let delaiJours = 15; // Par défaut : courrier simple
      const modeEnvoi = (data.modeEnvoi || data.typeEnvoi || '').toLowerCase().trim();
      if (modeEnvoi === 'email') {
        delaiJours = 7;
      } else if (modeEnvoi === 'lrar' || modeEnvoi === 'recommande_ar' || modeEnvoi.includes('rar') || modeEnvoi.includes('recommand')) {
        delaiJours = 21;
      }
      // postal = 15 jours (défaut)

      // Si le destinataire est un syndic → délai plus long
      const destType = (data.destinataireType || data.typeDestinataire || '').toLowerCase();
      if (destType.includes('syndic')) {
        delaiJours = Math.max(delaiJours, 21);
      }

      const dateRelance = new Date(dateEnvoi.getTime() + delaiJours * 24 * 60 * 60 * 1000);
      
      // Nom destinataire (string ou objet)
      const destNom = typeof data.destinataire === 'string'
        ? data.destinataire
        : data.destinataire?.nom || data.destinataire?.raisonSociale || data.destinataireNom || 'Destinataire inconnu';

      echeances.push({
        id: `courrier_${d.id}`,
        coproprieteId,
        titre: `Relance : ${data.objet || 'Courrier sans objet'}`,
        description: `Courrier envoyé le ${dateEnvoi.toLocaleDateString('fr-FR')} à ${destNom}. Délai de réponse raisonnable : ${delaiJours} jours.`,
        dateEcheance: Timestamp.fromDate(dateRelance),
        source: 'courrier',
        sourceId: d.id,
        sourceLabel: destNom,
        terminee: false,
        // Session 179 : Propager resolu depuis le courrier source
        resolu: data.resolu === true,
        createdAt: Timestamp.now(),
      });
    }
    
    console.log(`[Calendrier] Courriers agrégés: ${echeances.length}`);
  } catch (error) {
    console.error('[Calendrier] Erreur agrégation courriers:', error);
  }

  return echeances;
};

/** Construire les échéances depuis les dossiers avec date d'échéance */
const agregerDossiers = async (coproprieteId: string): Promise<Echeance[]> => {
  const echeances: Echeance[] = [];

  try {
    // Charger tous les dossiers — évite le besoin d'index composite
    const q = query(
      collection(db, 'affaires'),
      where('coproprieteId', '==', coproprieteId)
    );
    const snap = await getDocs(q);
    console.log(`[Calendrier] Dossiers trouvés: ${snap.size}`);

    for (const d of snap.docs) {
      const data = d.data();
      
      // Ignorer les dossiers clos
      if (data.statut === 'clos' || data.statut === 'ferme') continue;
      
      // Seuls les dossiers avec une dateEcheance explicite
      const dateEch = parseFirestoreDate(data.dateEcheance);
      if (!dateEch) continue;

      echeances.push({
        id: `dossier_${d.id}`,
        coproprieteId,
        titre: `Dossier : ${data.titre || 'Sans titre'}`,
        description: data.description || undefined,
        dateEcheance: Timestamp.fromDate(dateEch),
        source: 'dossier',
        sourceId: d.id,
        sourceLabel: data.titre || 'Dossier',
        terminee: false,
        // Session 179 : Propager resolu depuis le dossier source
        resolu: data.resolu === true,
        createdAt: Timestamp.now(),
      });
    }
    
    console.log(`[Calendrier] Dossiers agrégés: ${echeances.length}`);
  } catch (error) {
    console.error('[Calendrier] Erreur agrégation dossiers:', error);
  }

  return echeances;
};

// ============================================
// FONCTION PRINCIPALE — TOUTES LES ÉCHÉANCES
// ============================================

/** 
 * Charger TOUTES les échéances d'une copropriété :
 * - Manuelles (Firestore)
 * - Automatiques : garanties, courriers en attente, dossiers avec deadline
 * 
 * Enrichit chaque échéance avec joursRestants + urgence calculée.
 */
export const chargerToutesEcheances = async (coproprieteId: string): Promise<Echeance[]> => {
  console.log(`[Calendrier] Chargement toutes échéances pour coproprieteId: ${coproprieteId}`);
  
  // Lancer les 4 chargements en parallèle
  const [manuelles, garanties, courriers, dossiers] = await Promise.all([
    chargerEcheancesManuelles(coproprieteId),
    agregerGaranties(coproprieteId),
    agregerCourriers(coproprieteId),
    agregerDossiers(coproprieteId),
  ]);

  // Fusionner
  const toutes = [...manuelles, ...garanties, ...courriers, ...dossiers];
  console.log(`[Calendrier] TOTAL: ${toutes.length} échéances (${manuelles.length} manuelles, ${garanties.length} garanties, ${courriers.length} courriers, ${dossiers.length} dossiers)`);

  // Enrichir avec joursRestants + urgence
  for (const ech of toutes) {
    const dateEch = ech.dateEcheance?.toDate?.() || new Date(ech.dateEcheance as any);
    ech.joursRestants = calculerJoursRestants(dateEch);
    ech.urgence = calculerUrgence(ech.joursRestants);
  }

  // Trier par date (les plus proches en premier)
  toutes.sort((a, b) => (a.joursRestants ?? 0) - (b.joursRestants ?? 0));

  return toutes;
};

// ============================================
// UTILITAIRES DE FILTRAGE
// ============================================

/** Filtrer les échéances pour un mois donné */
export const filtrerParMois = (echeances: Echeance[], annee: number, mois: number): Echeance[] => {
  return echeances.filter((ech) => {
    const date = ech.dateEcheance?.toDate?.() || new Date(ech.dateEcheance as any);
    return date.getFullYear() === annee && date.getMonth() === mois;
  });
};

/** Filtrer les échéances à venir (non terminées, non dépassées ou dépassées récemment) */
export const filtrerAVenir = (echeances: Echeance[]): Echeance[] => {
  return echeances.filter((ech) => {
    if (ech.terminee) return false;
    return (ech.joursRestants ?? 0) >= -30;
  });
};

/** Compter les échéances urgentes (< 30 jours, non terminées) */
export const compterUrgentes = (echeances: Echeance[]): number => {
  return echeances.filter((ech) => {
    if (ech.terminee) return false;
    const jr = ech.joursRestants ?? 999;
    return jr <= 30;
  }).length;
};
